import { apiClient } from "./apiClient";

/**
 * Backed by audit.SecurityEventLog, a new table that records every auth
 * failure / geofence block / rate-limit / MFA-required / forbidden rejection
 * as it happens (previously these only ever hit ephemeral server logs,
 * nothing was queryable). failedLogins, geofenceViolations, deviceFingerprints
 * and suspiciousActivity below are all real now.
 *
 * Still GAP (no backend support at all yet, left as honest GapNotices in the
 * page rather than faked):
 *   - anomalies/riskScoring: would need a scoring model (rules-based or ML)
 *     analyzing login time-of-day, device changes, and location deltas per
 *     session and writing findings somewhere queryable -- app.UserSessions.
 *     risk_score exists in the schema but nothing ever calculates into it.
 *   - sessionReplay: would require a client-side session-recording library
 *     (e.g. rrweb) shipping redacted events to a new endpoint -- a real
 *     privacy/legal review is warranted given the explicit PII-redaction
 *     requirement, not just an engineering task.
 *   - threatIntel: needs an actual Microsoft Sentinel workspace + Graph
 *     Security API integration -- nothing to build here without that
 *     infrastructure existing first.
 *   - runbooks/incidentResponse: this one CAN be partially real without new
 *     infra -- see runbooks() below, which returns a static, hand-authored
 *     list of response actions mapped to userManagementApi calls that DO
 *     have real (if also-gapped) target endpoints, e.g. "force logout" and
 *     "suspend user" -- once those exist, one-click runbook actions work
 *     immediately with no separate runbook backend needed.
 */
export const securityMonitoringApi = {
  anomalies: (params) => apiClient.get("/management/security/anomalies", { params }).then((r) => r.data),
  riskScoring: (params) => apiClient.get("/management/security/risk-scores", { params }).then((r) => r.data),
  sessionReplay: (sessionId) => apiClient.get(`/management/security/session-replay/${sessionId}`).then((r) => r.data),
  threatIntelFeed: () => apiClient.get("/management/security/threat-intel").then((r) => r.data),

  /** Reaching this endpoint at all already proves the caller's IP passed
   * enforcement whenever `enforced` is true -- `enforced` is only true if
   * ADMIN_IP_ALLOWLIST is actually configured server-side. */
  ipAllowlistStatus: () => apiClient.get("/management/security/ip-allowlist-status").then((r) => r.data),

  failedLogins: (params) => apiClient.get("/management/security/failed-logins", { params }).then((r) => r.data),
  geofenceViolations: (params) => apiClient.get("/management/security/geofence-violations", { params }).then((r) => r.data),
  deviceFingerprints: () => apiClient.get("/management/security/device-fingerprints").then((r) => r.data),
  suspiciousActivity: (threshold) =>
    apiClient.get("/management/security/suspicious-activity", { params: threshold ? { threshold } : undefined }).then((r) => r.data),

  /** No backend call -- static runbook definitions describing which
   * userManagementApi/adminApi action each incident type should trigger. */
  runbooks: () => [
    { id: "compromised-account", title: "Suspected compromised account", steps: ["Force logout all sessions", "Revoke all trusted devices", "Suspend account pending review"] },
    { id: "impossible-travel", title: "Impossible travel detected", steps: ["Review session risk score breakdown", "Force logout the newer session", "Contact user to confirm"] },
    { id: "brute-force", title: "Repeated failed logins", steps: ["Check rate-limit status for the IP/fingerprint", "Temporarily block via geofence override (deny)", "Notify user of the attempts"] },
  ],
};
